/**
 * `NextStepCard` — the hero "Your next step" card. Shows the top-ranked
 * fix (or a calm "Nothing to fix right now." state when the ranker's
 * list is empty). A 3px coral or amber inset rule encodes the severity;
 * nothing else on the page is colored.
 *
 * 2026-10-02 rewrite (§H):
 *   - Removed the duplicate "See what we checked" link — the primary
 *     button opens the same drawer.
 *   - Replaced the "Not now" button with step navigation (prev / next
 *     arrows) so merchants can browse the backlog without dismissing.
 *   - A policy URL / return window / card-field step with a known value
 *     is directly fixable in the card: show the prefilled URL input +
 *     Save, or "Use <value>" right in the card.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ReadinessStep } from '@sill/types';

import { useC } from '../../lib/theme.js';
import { Kicker } from './MonoLink.js';
import { sansFont, serifFont } from './fonts.js';

export interface NextStepCardProps {
  readonly step: ReadinessStep | null;
  readonly step_index: number; // 0-based
  readonly step_total: number;
  /** Opens the Fix drawer for the current step. The primary button
   *  uses this unless an inline-fixable action is provided. */
  readonly onShowMeHow: () => void;
  /** Previous / next step navigation. Omit callbacks or set `disabled`
   *  via wrap-around at the parent. */
  readonly onPrev?: () => void;
  readonly onNext?: () => void;
  /** When the fix is one click (e.g. "Use 30 days"), the primary button
   *  does it directly instead of opening the drawer. */
  readonly primary?: { readonly label: string; readonly onClick: () => void } | null;
  /** Inline-fixable UI (e.g. prefilled policy URL input + Save). When
   *  provided, replaces the primary button on the right side of the
   *  card with a block of inline controls. */
  readonly inline_fix?: ReactNode;
  /** Discrepancy-specific "The card is right" (a real dismissal of a
   *  wrong flag). Rendered as a muted secondary. */
  readonly onDismissDiscrepancy?: (() => void) | null;
}

export function NextStepCard({
  step,
  step_index,
  step_total,
  onShowMeHow,
  onPrev,
  onNext,
  primary = null,
  inline_fix,
  onDismissDiscrepancy = null,
}: NextStepCardProps): JSX.Element {
  const C = useC();
  if (step === null) {
    return (
      <section
        aria-label="Nothing to fix right now"
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: '28px 32px',
          fontFamily: sansFont,
        }}
      >
        <div className="flex items-center gap-5">
          <div className="flex flex-col gap-1">
            <Kicker>You are set up</Kicker>
            <h2 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 26, color: C.text }}>
              Nothing to fix right now.
            </h2>
            <div style={{ fontSize: 14, color: C.body, maxWidth: 520, lineHeight: 1.55 }}>
              Agents can find, read and trust your site. We will keep rescanning on your schedule and surface anything new here.
            </div>
          </div>
        </div>
      </section>
    );
  }

  const rule = step.severity === 'block' ? C.coral : C.amber;
  const label = `${step_index + 1} of ${step_total}`;
  const canPrev = typeof onPrev === 'function' && step_total > 1;
  const canNext = typeof onNext === 'function' && step_total > 1;

  return (
    <section
      aria-label="Your next step"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        padding: '28px 32px',
        boxShadow: `inset 3px 0 0 ${rule}`,
        fontFamily: sansFont,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex flex-col gap-2 min-w-0" style={{ flex: 1 }}>
          <div className="flex items-center gap-3" style={{ color: C.muted }}>
            <Kicker>{`Your next step · ${label}`}</Kicker>
            <div role="group" aria-label="Step navigation" style={{ display: 'inline-flex', gap: 2 }}>
              <button
                type="button"
                onClick={onPrev}
                disabled={!canPrev}
                aria-label="Previous step"
                data-testid="next-step-prev"
                style={{
                  width: 24,
                  height: 24,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  color: canPrev ? C.text : C.dim,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  cursor: canPrev ? 'pointer' : 'default',
                  padding: 0,
                }}
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!canNext}
                aria-label="Next step"
                data-testid="next-step-next"
                style={{
                  width: 24,
                  height: 24,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  color: canNext ? C.text : C.dim,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  cursor: canNext ? 'pointer' : 'default',
                  padding: 0,
                }}
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
          <h2 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(24px, 3.4vw, 30px)', color: C.text, lineHeight: 1.15 }}>
            {step.headline}
          </h2>
          {step.evidence !== '' ? (
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: C.body, maxWidth: 640 }}>
              {step.evidence}
            </p>
          ) : null}
          {inline_fix !== undefined ? (
            <div style={{ marginTop: 10 }} data-testid="next-step-inline-fix">
              {inline_fix}
            </div>
          ) : null}
        </div>
        {inline_fix === undefined ? (
          <div className="flex flex-col gap-2 w-full md:w-auto" style={{ minWidth: 180 }}>
            <button
              type="button"
              onClick={primary?.onClick ?? onShowMeHow}
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                fontWeight: 600,
                background: C.lime,
                color: C.ink,
                border: `1px solid ${C.lime}`,
                borderRadius: 3,
                padding: '12px 18px',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              {primary?.label ?? 'Show me how'}
            </button>
            {onDismissDiscrepancy !== null ? (
              <button
                type="button"
                onClick={onDismissDiscrepancy}
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  background: 'transparent',
                  color: C.muted,
                  border: '1px solid transparent',
                  borderRadius: 3,
                  padding: '8px 14px',
                  cursor: 'pointer',
                }}
              >
                The card is right
              </button>
            ) : null}
          </div>
        ) : onDismissDiscrepancy !== null ? (
          <button
            type="button"
            onClick={onDismissDiscrepancy}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              background: 'transparent',
              color: C.muted,
              border: '1px solid transparent',
              borderRadius: 3,
              padding: '8px 14px',
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            The card is right
          </button>
        ) : null}
      </div>
    </section>
  );
}
